/**
 * brandsRemovalConfirmationComponent:
 *
 * This component handles the user interface for confirming a brands removal action within a dialog box.
 * It is responsible for:
 * 1. Displaying a form where the user can add a comment about the brands removal request.
 * 2. Validating the input comment to ensure it has a minimum number of words (3 words).
 * 3. Submitting the request to the API for processing, passing the necessary data (such as property brands IDs and the user’s comment).
 * 4. Closing the dialog with or without submitting data depending on the user's interaction.
 *
 * The component is triggered by an action in the parent component and is expected to receive:
 * - The selected entity type (which could represent a specific property or other data entity) from the parent.
 * - A list of property brands IDs that are associated with the removal request.
 *
 * Methods:
 * - `ngOnInit`: Initializes the form and sets up the selected entity type for brands removal.
 * - `close`: Closes the dialog without performing any action.
 * - `deletebrandsRequest`: Submits the brands removal request to the API after validating the comment.
 * - `comment`: Getter for the comment form control to facilitate easier validation and access.
 *
 * Dependencies:
 * - `ApiserviceService`: Used for making API calls to submit the brands removal request.
 * - `MatDialogRef`: Provides methods for managing the dialog, such as closing it with a response.
 * - `MAT_DIALOG_DATA`: Provides data passed to the dialog, including the selected entity and brands IDs.
 *
 * @selector app-brands-removal-confirmation
 * @templateUrl ./brands-removal-confirmation.component.html
 * @styleUrls ['./brands-removal-confirmation.component.scss']
 */

import { Component, Inject, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { minWordsValidator } from '../../../admin-attribute-deletion/validations/minWordsValidator';
import { ApiserviceService } from '../../../../apiservice.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-brands-removal-confirmation',
  templateUrl: './brands-removal-confirmation.component.html',
  styleUrls: ['./brands-removal-confirmation.component.scss']
})
export class BrandsRemovalConfirmationComponent implements OnDestroy {
  /** Selected entity type for which brands removal is being requested. */
  selectedEntityType: string = "";

  /** Form group to hold the comment input with validation. */
  deleteRequestGroup: FormGroup;

  /** Log IDs to track related logs during the deletion process. */
  logId: string[] = [];
  private subs = new Subscription();

  /**
   * Constructor to initialize the component.
   *
   * @param apiService Service to handle API requests.
   * @param fb FormBuilder to build form controls.
   * @param dialogRef Reference to the dialog window for closing and passing data.
   * @param data Injected data passed to the dialog, including selected entity and brands IDs.
   */
  constructor(
    public apiService: ApiserviceService,
    public fb: FormBuilder,
    public dialogRef: MatDialogRef<BrandsRemovalConfirmationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  /**
   * ngOnInit lifecycle hook to initialize form controls and set selected entity type.
   *
   * @returns void
   */
  ngOnInit(): void {
    // Set the selected entity type passed from the dialog data
    this.selectedEntityType = this.data.selectedEntity;

    // Initialize the form group with a comment field having a minimum word count validator
    this.deleteRequestGroup = this.fb.group({
      comment: ["", [Validators.required, minWordsValidator(3)]],
    });
  }

  /**
   * Closes the dialog without any action.
   *
   * @returns void
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Sends the brands removal request to the API after validating the form.
   * The request includes the selected property brands IDs and the user's comment.
   *
   * @returns void
   */
  deleteBrandsRequest(): void {
    // Prepare the request body with necessary details
    const body = {
      brand_ids: this.data.brand_ids,
      user_id: this.apiService.user_id,
      remark: this.deleteRequestGroup.value.comment,
      filter: this.data.filter,
      is_delete_by_filter: this.data.is_delete_by_filter,
    };

    // Define the API endpoint for brands removal
    const url = `brand-upload/delete`;

    // Call the API service to submit the request
    const deleteBrandsSub = this.apiService.postCsvData(url, body).subscribe({
      next: (res: any) => {
        console.log(res, "res");
        // Close the dialog and pass the response data upon success
        this.dialogRef.close(res?.data);
      },
      error: (err: any) => {
        // Handle error if any occurs during the API request
        console.error('Error during brands removal:', err);
      },
    });
    this.subs.add(deleteBrandsSub);
  }

  /**
   * Getter for the 'comment' form control.
   *
   * @returns AbstractControl
   */
  get comment(): AbstractControl {
    return this.deleteRequestGroup.get('comment')!;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
